import React, {FC, useMemo} from 'react';

import {useThreadTurns} from '../wfReactInterface/tsDataModelHooks';

export interface ThreadMetadataProps {
  turnsState: ReturnType<typeof useThreadTurns>['turnsState'];
}

/**
 * ThreadMetadata displays calculated metrics for a thread.
 *
 * Shows turn count, p50 latency, and p99 latency in a clean grid layout.
 *
 * @param turnsState - State object containing turns data from useThreadTurns hook
 *
 * @example
 * <ThreadMetadata turnsState={turnsState} />
 */
export const ThreadMetadata: FC<ThreadMetadataProps> = ({turnsState}) => {
  const metrics = useMemo(() => {
    if (!turnsState.value || turnsState.loading) {
      return {
        turnCount: 0,
        p50Latency: '-',
        p99Latency: '-',
      };
    }

    const turns = turnsState.value;
    const turnCount = turns.thread.turn_count;

    const p50Latency = `${(
      (turns.thread.p50_turn_duration_ms ?? 0) / 1e3
    ).toFixed(3)}s`;
    const p99Latency = `${(
      (turns.thread.p99_turn_duration_ms ?? 0) / 1e3
    ).toFixed(3)}s`;

    return {
      turnCount,
      p50Latency,
      p99Latency,
    };
  }, [turnsState]);

  return (
    <div className="flex h-[51px] w-full items-center gap-x-24 border-b border-solid border-moon-200 px-16">
      <div>
        <p className="text-sm text-moon-500">Turns</p>
        <p className="truncate text-sm font-semibold text-moon-800">
          {metrics.turnCount}
        </p>
      </div>
      <div>
        <p className="text-sm text-moon-500">p50 latency</p>
        <p className="truncate text-sm font-semibold text-moon-800">
          {metrics.p50Latency}
        </p>
      </div>
      <div>
        <p className="text-sm text-moon-500">p99 latency</p>
        <p className="truncate text-sm font-semibold text-moon-800">
          {metrics.p99Latency}
        </p>
      </div>
    </div>
  );
};
